import { Link } from 'react-router-dom'
import { Briefcase, MoreHorizontal } from 'lucide-react'
import { getStageColor, getStageLabel } from '@/components/Deals/deals-constants'
import { formatDashboardCurrency } from '@/utils/crmValuesDisplay'
import { formatDate } from '@/utils/format-date'
import { dealsPagePathForDeal } from '@/utils/dealsPageNavigation'
import type { DashboardLatestDeals } from '@/hooks/dashboardDataTypes'
import type { DealStage } from '@/types'
import { cn } from '@/utils/cn'

type Props = {
  deals: DashboardLatestDeals[]
  valuesVisible?: boolean
}

function stageLabel(etapa: string): string {
  const key = etapa.trim().toLowerCase() as DealStage
  return getStageLabel(key)
}

function stageColor(etapa: string): string {
  const key = etapa.trim().toLowerCase() as DealStage
  return getStageColor(key)
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

export function OperacaoLatestDeals({ deals, valuesVisible = false }: Props) {
  return (
    <div className='rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] shadow-sm'>
      <div className='flex flex-wrap items-end justify-between gap-3 border-b border-[var(--border)] px-5 py-4'>
        <div>
          <div className='flex items-center gap-2'>
            <Briefcase size={16} className='text-accent' aria-hidden />
            <h3 className='text-base font-semibold text-text-primary'>Últimos negócios</h3>
          </div>
          <p className='mt-1 text-sm text-text-muted'>
            {deals.length} registro{deals.length === 1 ? '' : 's'} no período selecionado
          </p>
        </div>
        <Link
          to='/deals'
          className='rounded-full border border-[var(--border)] px-3 py-1 text-xs font-semibold text-text-secondary hover:bg-[var(--bg-dark)]/20'
        >
          Ver todos
        </Link>
      </div>

      {!deals?.length ? (
        <p className='px-5 py-10 text-center text-sm text-text-muted'>Nenhum negócio no período.</p>
      ) : (
        <div className='overflow-x-auto'>
          <table className='min-w-full text-left text-sm'>
            <thead>
              <tr className='border-b border-[var(--border)] text-xs uppercase tracking-wide text-text-muted'>
                <th className='px-5 py-3 font-semibold'>Negócio</th>
                <th className='px-3 py-3 font-semibold'>Valor</th>
                <th className='px-3 py-3 font-semibold'>Etapa</th>
                <th className='px-3 py-3 font-semibold'>Responsável</th>
                <th className='px-5 py-3 font-semibold'>Atualizado</th>
                <th className='px-3 py-3' aria-hidden />
              </tr>
            </thead>
            <tbody>
              {deals.map((deal) => {
                const color = stageColor(deal.etapa)
                return (
                  <tr
                    key={deal.id}
                    className='border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-dark)]/10'
                  >
                    <td className='px-5 py-3.5'>
                      <Link to={dealsPagePathForDeal(deal.id)} className='flex min-w-[220px] items-center gap-3'>
                        <span
                          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white'
                          style={{ backgroundColor: color }}
                        >
                          {initials(deal.empresa || deal.nome)}
                        </span>
                        <span className='min-w-0'>
                          <span className='block truncate font-semibold text-text-primary'>{deal.nome}</span>
                          <span className='block truncate text-xs text-text-muted'>{deal.empresa}</span>
                        </span>
                      </Link>
                    </td>
                    <td className='px-3 py-3.5 font-semibold tabular-nums text-text-primary'>
                      {formatDashboardCurrency(deal.valor, valuesVisible)}
                    </td>
                    <td className='px-3 py-3.5'>
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold',
                        )}
                        style={{
                          backgroundColor: `color-mix(in srgb, ${color} 18%, transparent)`,
                          color,
                        }}
                      >
                        {stageLabel(deal.etapa)}
                      </span>
                    </td>
                    <td className='px-3 py-3.5 text-text-secondary'>{deal.owner}</td>
                    <td className='px-5 py-3.5 text-text-muted'>
                      {formatDate(deal.dataFechamento ?? deal.createdAt)}
                    </td>
                    <td className='px-3 py-3.5 text-text-muted'>
                      <MoreHorizontal size={16} aria-hidden />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
